import { useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { useExportDeliveries } from '@/services/db/delivery/mutations';
import type { DeliveryExportInput } from '@/services/db/delivery/utils';
import { DeliveryDateRange } from './delivery-date-range';

export interface ExportDeliveriesModalData {
  /** The current filter values (channel/status/campaign/action/drafted) to export. */
  exportInput: DeliveryExportInput;
  /** Analytics source id: `outbox_deliveries` or `outbox_drafts`. */
  from?: string;
  workspaceId: number;
}

/**
 * "Export to CSV" modal. Collects an export-specific date range (independent of
 * the page's filter range — the Ember `exportStartTs`/`exportEndTs`), fires the
 * export, and points the user to the exports page. Ported from the Ember
 * `cio-export-button` CSV flow (a snackbar replaces Ember's async-result modal).
 */
export function ExportDeliveriesModal({
  data,
  onClose,
  modalState,
}: PlumaManagedModalProps<ExportDeliveriesModalData>) {
  const exportDeliveries = useExportDeliveries({ workspaceId: data.workspaceId });
  const snackbars = useSnackbars();
  const router = useRouter();
  // Seed the last 30 days (Ember default) so an immediate export has a real range —
  // an empty range falls back to 0/0 in `buildDeliveriesExportBody`, exporting all-time.
  const [range, setRange] = useState<{ startTs: number | null; endTs: number | null }>(() => {
    const nowSeconds = Math.floor(Date.now() / 1000);
    return { startTs: nowSeconds - 30 * 24 * 60 * 60, endTs: nowSeconds };
  });

  const exportsHref = router.buildLocation({
    to: '/workspaces/$workspaceId/exports',
    params: { workspaceId: data.workspaceId },
  }).href;

  const handleExport = async () => {
    if (exportDeliveries.isPending) return;
    try {
      await exportDeliveries.mutateAsync({
        ...data.exportInput,
        exportStartTs: range.startTs ?? undefined,
        exportEndTs: range.endTs ?? undefined,
        from: data.from,
      });
      snackbars.create({
        id: 'delivery.export',
        variant: 'success',
        message: 'Your export is processing. When it is ready, it will be available on the exports page.',
        action: { label: 'Go to Exports', href: exportsHref, isExternal: true },
      });
      onClose();
    } catch {
      snackbars.create({
        id: 'delivery.export.error',
        variant: 'critical',
        message: 'There was a problem processing your export. Please try again.',
      });
    }
  };

  return (
    <Modal title="Export to CSV" state={modalState}>
      <ModalBody>
        <Stack gap="150">
          <Text>Select start and end date</Text>
          <DeliveryDateRange
            startTs={range.startTs}
            endTs={range.endTs}
            onChange={setRange}
            isClearable={false}
            maxRange="6 months"
          />
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={() => onClose()}>
          Close
        </Button>
        <Button variant="primary" onClick={handleExport} isLoading={exportDeliveries.isPending}>
          Export
        </Button>
      </ModalFooter>
    </Modal>
  );
}
